import React, { useCallback } from 'react';
import { PropTypes } from 'prop-types';
import {
  IconButton,
  DialogContentText,
  TableCell,
  TableRow
} from '@material-ui/core';
import { useIntl } from 'react-intl';
import { RemoveCircleSharp as RemoveCircle } from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { RemoveEntityConfirmationDialog } from '~/modules/common/components';
import { useDialogState } from '~/modules/common/hooks';
import SkillRating from '~/modules/resourcing/common/components/Skills/SkillRating';

const deleteDialogResourceKeys = {
  header: 'skillInfoCard.deleteSkillDialog.removeUserDefinedSkill',
  actionButton: 'skillInfoCard.deleteSkillDialog.removeUserDefinedSkill'
};

const useStyles = makeStyles(theme => ({
  removeIcon: {
    color: theme.palette.secondary.main
  },
  iconButton: {
    padding: 0
  },
  cell: {
    borderBottom: 'none',
    padding: theme.spacing(0.5)
  },
  nameCell: {
    borderBottom: 'none',
    padding: theme.spacing(0.5),
    position: 'sticky',
    left: 0,
    backgroundColor: theme.palette.background.paper,
    zIndex: 1
  },
  name: {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    width: '80%',
    paddingTop: theme.spacing(0.25),
    paddingLeft: theme.spacing(3)
  },
  ratingCell: {
    fontWeight: theme.typography.fontWeightRegular,
    textAlign: 'center',
    borderBottom: 'none',
    fontSize: '11px'
  },
  removeIconCell: {
    borderBottom: 'none',
    textAlign: 'right',
    padding: theme.spacing(0.5),
    '&:last-child': {
      paddingRight: '5px'
    },
    position: 'sticky',
    right: 0,
    backgroundColor: theme.palette.common.white
  }
}));

export const UserDefinedSkillRow = ({
  className,
  userDefinedSkill,
  skillsettings,
  classes: classesOverride,
  onUserDefinedSkillRemove,
  maxSkillLevel,
  onUserDefinedSkillRatingUpdate
}) => {
  const { formatMessage } = useIntl();

  const classes = useStyles({ classes: classesOverride });
  const {
    open: showRemoveDialog,
    openDialog: openRemoveDialog,
    closeDialog: closeRemoveDialog
  } = useDialogState(false);

  const onRemoveCallback = useCallback(
    () => onUserDefinedSkillRemove(userDefinedSkill),
    [onUserDefinedSkillRemove, userDefinedSkill]
  );

  const onUDSkillRatingUpdate = useCallback(
    ({ ratingIndex }) => {
      onUserDefinedSkillRatingUpdate({ userDefinedSkill, ratingIndex });
    },
    [onUserDefinedSkillRatingUpdate, userDefinedSkill]
  );

  return (
    <>
      {showRemoveDialog && (
        <RemoveEntityConfirmationDialog
          resourceKeys={deleteDialogResourceKeys}
          open={showRemoveDialog}
          onClose={closeRemoveDialog}
          onConfirm={onRemoveCallback}
        >
          <DialogContentText>
            {formatMessage({
              id: 'skillInfoCard.deleteSkillDialog.description'
            })}
          </DialogContentText>
        </RemoveEntityConfirmationDialog>
      )}
      <TableRow className={className}>
        <TableCell className={classes.nameCell}>
          <div className={classes.name}>{userDefinedSkill.displayText}</div>
        </TableCell>
        <TableCell className={classes.ratingCell}>
          {Boolean(maxSkillLevel) && (
            <SkillRating
              skillAssignment={userDefinedSkill}
              onSkillRatingUpdate={onUDSkillRatingUpdate}
              editable
              maxSkillLevel={maxSkillLevel}
            />
          )}
        </TableCell>
        <TableCell className={classes.cell}></TableCell>
        {skillsettings.map(setting => (
          <TableCell className={classes.cell} key={setting.uri} />
        ))}
        <TableCell className={classes.removeIconCell}>
          <IconButton
            className={classes.iconButton}
            onClick={openRemoveDialog}
            aria-label={formatMessage({ id: 'button.delete' })}
          >
            <RemoveCircle />
          </IconButton>
        </TableCell>
      </TableRow>
    </>
  );
};

UserDefinedSkillRow.propTypes = {
  classes: PropTypes.object,
  userDefinedSkill: PropTypes.object.isRequired,
  onUserDefinedSkillRemove: PropTypes.func,
  onUserDefinedSkillRatingUpdate: PropTypes.func,
  skillsettings: PropTypes.array,
  maxSkillLevel: PropTypes.number,
  className: PropTypes.string
};

export default UserDefinedSkillRow;
